import { RU_RESOURCES } from './resources.ru.ts'
import { ADDITIONAL_EN_RESOURCES } from './resources.en.ts'
import { GTM_EN_RESOURCES } from './resources.gtm.en.ts'

export type ResourceLanguage = 'en' | 'ru'
export type ResourceType = 'guide' | 'use-case' | 'research'

export type ResourceCtaType =
  | 'context-audit'
  | 'build-agent'
  | 'book-demo'
  | 'find-customers'

export type ResourceContentBlock =
  | {
      readonly type: 'paragraph'
      readonly text: string
    }
  | {
      readonly type: 'list'
      readonly items: ReadonlyArray<string>
    }
  | {
      readonly type: 'callout'
      readonly title: string
      readonly body: string
    }
  | {
      readonly type: 'source'
      readonly label: string
      readonly href: string
      readonly description?: string
    }
  | {
      readonly type: 'metrics'
      readonly items: ReadonlyArray<{
        readonly value: string
        readonly label: string
        readonly detail?: string
      }>
      readonly sourceNote?: string
    }
  | {
      readonly type: 'figure'
      readonly src: string
      readonly alt: string
      readonly caption?: string
    }
  | {
      readonly type: 'table'
      readonly columns: readonly [string, string]
      readonly rows: ReadonlyArray<readonly [string, string]>
    }
  | {
      readonly type: 'faq'
      readonly items: ReadonlyArray<{
        readonly question: string
        readonly answer: string
      }>
    }

export interface ResourceSection {
  readonly id: string
  readonly title: string
  readonly level: 2 | 3
  readonly blocks: ReadonlyArray<ResourceContentBlock>
}

export interface Resource {
  readonly language: ResourceLanguage
  readonly title: string
  readonly slug: string
  readonly type: ResourceType
  readonly description: string
  readonly author: string
  readonly publishedAt: string
  readonly updatedAt?: string
  readonly readingTime?: string
  readonly coverImage?: string
  readonly coverImageAlt?: string
  readonly topics?: ReadonlyArray<string>
  readonly integrations?: ReadonlyArray<string>
  readonly featured?: boolean
  readonly published: boolean
  readonly seoTitle?: string
  readonly seoDescription?: string
  readonly relatedResources?: ReadonlyArray<string>
  readonly ctaType?: ResourceCtaType
  readonly keyTakeaways: ReadonlyArray<string>
  readonly sections: ReadonlyArray<ResourceSection>
}

export interface ResourceTypeConfig {
  readonly label: string
  readonly pluralLabel: string
  readonly segment: string
}

export const RESOURCE_TYPE_CONFIG: Record<
  ResourceLanguage,
  Record<ResourceType, ResourceTypeConfig>
> = {
  en: {
    guide: { label: 'Guide', pluralLabel: 'Guides', segment: 'guides' },
    'use-case': {
      label: 'Use case',
      pluralLabel: 'Use cases',
      segment: 'use-cases',
    },
    research: {
      label: 'Research',
      pluralLabel: 'Research',
      segment: 'research',
    },
  },
  ru: {
    guide: { label: 'Гайд', pluralLabel: 'Гайды', segment: 'guides' },
    'use-case': {
      label: 'Сценарий',
      pluralLabel: 'Сценарии',
      segment: 'use-cases',
    },
    research: {
      label: 'Исследование',
      pluralLabel: 'Исследования',
      segment: 'research',
    },
  },
}

/**
 * The content catalog for Resources. Add an entry here to create its card,
 * article route, metadata, related-resource links and sitemap entry.
 */
const EN_RESOURCES: ReadonlyArray<Resource> = [
  {
    language: 'en',
    type: 'guide',
    slug: 'what-is-context-debt',
    title: 'What Is Context Debt?',
    description:
      'Learn how fragmented, outdated, and missing context slows teams down—and how to identify it.',
    author: 'Laplace Team',
    publishedAt: '2026-08-21',
    readingTime: '6 min read',
    coverImage: '/images/context-debt-guide.png',
    coverImageAlt:
      'Laplace connects fragmented work sources and reveals context gaps in a unified view',
    topics: ['Context Debt', 'Context Audit', 'AI readiness'],
    integrations: ['Jira', 'Confluence', 'GitHub'],
    featured: true,
    published: false,
    seoTitle: 'What Is Context Debt? A Practical Guide — Laplace',
    seoDescription:
      'Learn what Context Debt is, how fragmented and outdated knowledge slows teams down, and how to identify it before building AI agents.',
    relatedResources: ['digital-employees', 'choose-ai-workflow'],
    ctaType: 'context-audit',
    keyTakeaways: [
      'Context Debt is the cost of fragmented, outdated, or missing information across a business process.',
      'It affects people and AI agents alike: both make slower, less reliable decisions when context is incomplete.',
      'A Context Audit turns the problem into a measurable map of sources, gaps, owners, and next actions.',
    ],
    sections: [
      {
        id: 'what-context-debt-means',
        title: 'What Context Debt means',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Every process depends on context: the current plan, customer history, decisions, policies, and the state of work. Context Debt accumulates when that information is split between systems, no longer current, or never captured in the first place.',
          },
          {
            type: 'paragraph',
            text: 'The result is familiar. Teams search across tools, repeat questions, reconstruct decisions, and verify information before they can act. An AI agent inherits the same uncertainty unless the underlying context is made explicit and manageable.',
          },
          {
            type: 'callout',
            title: 'Context Debt is operational debt',
            body: 'It is not simply a documentation problem. It changes cycle time, decision quality, onboarding speed, and the reliability of automated work.',
          },
        ],
      },
      {
        id: 'how-it-appears',
        title: 'How it appears in day-to-day work',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Context Debt is easiest to spot at the handoffs in a workflow. Look for moments where a person needs to pause, ask, cross-check, or copy information before the next step can begin.',
          },
          {
            type: 'list',
            items: [
              'A project status in Jira conflicts with the latest decision in a meeting note.',
              'A support specialist cannot see why a product exception was approved.',
              'A new teammate needs several people to explain how a recurring process actually works.',
              'An AI assistant gives a plausible answer but cannot identify the source or its freshness.',
            ],
          },
        ],
      },
      {
        id: 'why-agents-expose-it',
        title: 'Why AI agents expose hidden context gaps',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'A person can compensate for missing context through experience and informal relationships. An agent needs clearer instructions, reliable sources, defined tools, and approval boundaries. That makes an agent initiative a useful stress test for the process itself.',
          },
        ],
      },
      {
        id: 'instructions-and-boundaries',
        title: 'Instructions are only one part of the system',
        level: 3,
        blocks: [
          {
            type: 'paragraph',
            text: 'A strong prompt cannot repair a missing decision log or an outdated policy. Managed agents work best when instructions, source context, permissions, and human approvals are designed together.',
          },
          {
            type: 'figure',
            src: '/images/context-debt-instructions.png',
            alt: 'A managed AI agent connects instructions, documents, processes, and roles with human approval',
            caption:
              'Reliable results emerge when instructions work together with context, tools, permissions, and human control.',
          },
        ],
      },
      {
        id: 'start-with-an-audit',
        title: 'Start with a Context Audit',
        level: 2,
        blocks: [
          {
            type: 'paragraph',
            text: 'Choose one meaningful workflow and map the systems, documents, conversations, owners, and approvals it relies on. Then assess which context is fragmented, outdated, missing, or inaccessible to the people and agents who need it.',
          },
          {
            type: 'list',
            items: [
              'Define the workflow and the decision it is meant to support.',
              'Identify the authoritative source for each critical piece of context.',
              'Measure the time spent searching, checking, and reconstructing information.',
              'Fix the highest-impact gaps before expanding automation.',
            ],
          },
          {
            type: 'callout',
            title: 'Keep the first scope narrow',
            body: 'A focused audit produces a concrete improvement plan. A company-wide inventory often creates another source of stale context.',
          },
        ],
      },
    ],
  },
  ...GTM_EN_RESOURCES,
  ...ADDITIONAL_EN_RESOURCES,
]

export const RESOURCES: ReadonlyArray<Resource> = [
  ...EN_RESOURCES,
  ...RU_RESOURCES,
]

export function isPublishedResource(resource: Resource): boolean {
  return resource.published
}

export function getPublishedResources(
  language: ResourceLanguage = 'en',
  source: ReadonlyArray<Resource> = RESOURCES,
): Array<Resource> {
  return source.filter(
    (resource) =>
      isPublishedResource(resource) && resource.language === language,
  )
}

export function getResourcePath(resource: Resource): string {
  return `${getResourcesIndexPath(resource.language)}/${RESOURCE_TYPE_CONFIG[resource.language][resource.type].segment}/${resource.slug}`
}

export function getResourcesIndexPath(language: ResourceLanguage): string {
  return language === 'ru' ? '/ru/resources' : '/resources'
}

export function getResourceTypeFromSegment(
  segment: string,
  language: ResourceLanguage = 'en',
): ResourceType | undefined {
  return (
    Object.entries(RESOURCE_TYPE_CONFIG[language]) as Array<
      [ResourceType, ResourceTypeConfig]
    >
  ).find(([, config]) => config.segment === segment)?.[0]
}

export function findPublishedResourceByPath(
  category: string,
  slug: string,
  language: ResourceLanguage = 'en',
): Resource | undefined {
  const type = getResourceTypeFromSegment(category, language)
  if (!type) return undefined

  return getPublishedResources(language).find(
    (resource) => resource.type === type && resource.slug === slug,
  )
}

export function getFeaturedResource(
  language: ResourceLanguage = 'en',
): Resource | undefined {
  const published = getPublishedResources(language)
  return published.find((resource) => resource.featured) ?? published[0]
}

export function getResourcesByType(
  type: ResourceType,
  language: ResourceLanguage = 'en',
): Array<Resource> {
  return getPublishedResources(language).filter(
    (resource) => resource.type === type,
  )
}

export function getRelatedResources(resource: Resource): Array<Resource> {
  if (!resource.relatedResources?.length) return []

  const relatedSlugs = new Set(resource.relatedResources)
  return getPublishedResources(resource.language).filter(
    (candidate) =>
      candidate.slug !== resource.slug && relatedSlugs.has(candidate.slug),
  )
}
